import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, TextField, debounce } from '@mui/material';
import { InsuranceProviderService } from '../../service/insuranceProviderService';

/**
 * Debounced provider autocomplete.
 * value: { id, name } | null
 * onChange: (provider | null) => void
 */
export default function ProviderAutocomplete({
  value = null,
  onChange,
  error = false,
  helperText = '',
  required = false,
  disabled = false,
  label = 'Insurance Provider'
}) {
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);

  // When value changes externally (e.g. dialog opens with existing policy), sync input label
  useEffect(() => {
    if (value?.name) {
      setInputValue(value.name);
    }
  }, [value?.id, value?.name]);

  const fetchProviders = useMemo(() => debounce(async (query, callback) => {
    try {
      const result = await InsuranceProviderService.search(query);
      callback(result);
    } catch (_) {
      callback({ code: 1 });
    }
  }, 400), []);

  useEffect(() => {
    let active = true;

    setLoading(true);
    fetchProviders(inputValue.trim(), (result) => {
      if (!active) return;
      setLoading(false);

      const { code, data } = result || {};
      if (code !== 0) {
        setOptions(value ? [value] : []);
        return;
      }

      // Ensure current value stays in list
      const list = data || [];
      if (value && !list.find((p) => p.id === value.id)) {
        setOptions([value, ...list]);
      } else {
        setOptions(list);
      }
    });

    return () => { active = false; };
  }, [fetchProviders, inputValue, value]);

  return (
    <Autocomplete
      fullWidth
      disabled={disabled}
      filterOptions={(x) => x}
      options={options}
      value={value}
      loading={loading}
      autoComplete
      includeInputInList
      filterSelectedOptions
      isOptionEqualToValue={(option, selected) => option.id === selected?.id}
      getOptionLabel={(option) => option?.name || ''}
      onChange={(_, newValue) => onChange?.(newValue || null)}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
    />
  );
}
